import type { StoreProvider } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { refreshProvider } from "../../core/ops/provider-ops";
import { errorText } from "../../core/primitives/errors";
import { stripeConnection } from "../../core/primitives/onboarding";
import { SetupChecklistActions, SetupChecklistState } from "./setup-checklist-model";

export type SetupChecklistDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "providersRepository">;

/** Mirrors the selected shop's Stripe connection; the shell keeps the providers fresh. */
export class SetupChecklistController extends BaseController implements SetupChecklistActions {
  readonly state = new SetupChecklistState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: SetupChecklistDeps,
  ) {
    super();
    subtree.put(SetupChecklistState, this.state);
    subtree.put(SetupChecklistActions, this);
    this.sync(() => {
      const selected = deps.storeRepository.selectedStore;
      const repo = deps.providersRepository;
      // Finishing setup is Owner-only on the backend (see resolveOnboardingStep),
      // so a Manager or Staff member is never offered the checklist either —
      // there is nothing here they could act on.
      const store = selected?.myRole === "OWNER" ? selected : null;
      this.state.store.value = store;
      if (!store) {
        this.state.stripe.value = null;
        this.state.stripeKnown.value = false;
        return;
      }
      const known = repo.isLoadedFor(store.id);
      const row = stripeConnection(store);
      const fallback: StoreProvider | null = row ? { ...row, merchantAction: null } : null;
      this.state.stripe.value = known ? (repo.find("stripe") ?? fallback) : fallback;
      this.state.stripeKnown.value = known;
    }, [deps.storeRepository, deps.providersRepository]);
  }

  checkAgain = async () => {
    const store = this.state.store.value;
    if (!store || this.state.checking.value) return;
    this.state.checking.value = true;
    this.state.checkError.value = null;
    try {
      const r = await refreshProvider({ ...this.deps, storeId: store.id, providerKey: "stripe" });
      if (!r.ok) this.state.checkError.value = errorText(r.errors);
    } finally {
      this.state.checking.value = false;
    }
  };
}
